import { useState, useEffect, useMemo } from "react";
import { useAuthContext } from "../context/auth";
import { loadExchangeRates, getCrossRate, isLoaded } from "./exchangeRateService";

/**
 * Hook compartido para las 9 herramientas de Análisis Categorías.
 * Provee formateo de moneda y conversión CLP→PEN para Perú.
 *
 * - Chile: valores en CLP ($), locale es-CL, sin conversión
 * - Perú: valores CLP se convierten a PEN (S/) via cross-rate, locale es-PE
 * - USD: universal, se mantiene en US$ para ambos países
 *
 * Tipos de cambio delegados a exchangeRateService.js
 */
export const useCategoriaMoneda = () => {
  const { pais } = useAuthContext();
  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";
  const isPeru = normalizedPais === "Perú";
  const locale = isPeru ? "es-PE" : "es-CL";
  const monedaLocal = isPeru ? "PEN" : "CLP";
  const prefijo = isPeru ? "S/ " : "$ ";

  const [loaded, setLoaded] = useState(isLoaded());

  useEffect(() => {
    if (isLoaded()) {
      setLoaded(true);
      return;
    }
    loadExchangeRates().then(() => setLoaded(true));
  }, []);

  // Cross-rate: CLP → PEN = USD_PEN / USD_CLP
  const crossRate = useMemo(() => {
    if (!isPeru) return 1;
    if (loaded) return getCrossRate() || 0;
    return 0;
  }, [isPeru, loaded]);

  /** Convierte un monto CLP a moneda local (identidad en Chile, cross-rate en Perú) */
  const convertCLP = (amountCLP) => {
    if (!amountCLP) return 0;
    return isPeru ? amountCLP * crossRate : amountCLP;
  };

  /** Formatea con prefijo de moneda local ($ o S/) y locale correspondiente */
  const formatLocal = (amount) => {
    if (amount == null || isNaN(amount)) return "-";
    return `${prefijo}${Math.round(amount).toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  /** Formatea en USD con locale correspondiente */
  const formatUSD = (num) => {
    if (num == null || isNaN(num)) return "-";
    return `US$ ${Math.round(num).toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  /** Formatea número sin prefijo de moneda, con locale correspondiente */
  const formatNumber = (num) => {
    if (num == null || isNaN(num)) return "-";
    return Math.round(num).toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return {
    pais: normalizedPais,
    isPeru,
    locale,
    monedaLocal,
    prefijo,
    crossRate,
    loading: !loaded,
    convertCLP,
    formatLocal,
    formatUSD,
    formatNumber,
  };
};
